import { MoneyMapFilterState, MoneyMapVisual } from '@fruitful/money-map';
import { MoneyMapVisual as LegacyMoneyMapVisual } from '@fruitful/money-map/src/components/MoneyMapVisual';
import { Redirect } from 'expo-router';
import { useState } from 'react';
import {
    BodySmallRegular,
    LabelEmphasized,
    LabelRegular,
    MoneyMapPreview,
    ScreenContainer,
    XStack,
    YStack,
} from 'smoothie';

// ---------------------------------------------------------------------------
// Data scenarios — shared across all sections
// ---------------------------------------------------------------------------

const SCENARIOS = [
    {
        id: 'balanced',
        label: 'Balanced',
        description: 'All three categories have value',
        incomeTotalCents: 750000,
        billsTotalCents: 350000,
        spendTotalCents: 150000,
        goalsTotalCents: 250000,
    },
    {
        id: 'no-goals',
        label: 'No Goals',
        description: 'Goals = $0 (transparent bar in new, hidden in old)',
        incomeTotalCents: 750000,
        billsTotalCents: 450000,
        spendTotalCents: 300000,
        goalsTotalCents: 0,
    },
    {
        id: 'bills-spend-only',
        label: 'Bills + Spend only',
        description: 'Goals = $0, two visible bars',
        incomeTotalCents: 500000,
        billsTotalCents: 300000,
        spendTotalCents: 200000,
        goalsTotalCents: 0,
    },
    {
        id: 'five-six-five-zero',
        label: '$5,000 / $6,500 / $0',
        description: 'Bills = $5,000, Spend = $6,500, Goals = $0',
        incomeTotalCents: 1150000,
        billsTotalCents: 500000,
        spendTotalCents: 650000,
        goalsTotalCents: 0,
    },
    {
        id: 'five-six-point-five',
        label: '$5,000 / $6,000 / $500',
        description: 'Bills = $5,000, Spend = $6,000, Goals = $500',
        incomeTotalCents: 1150000,
        billsTotalCents: 500000,
        spendTotalCents: 600000,
        goalsTotalCents: 50000,
    },
    {
        id: 'figma-default',
        label: '$5,000 / $3,000 / $3,500',
        description: 'Bills = $5,000, Spend = $3,000, Goals = $3,500',
        incomeTotalCents: 1150000,
        billsTotalCents: 500000,
        spendTotalCents: 300000,
        goalsTotalCents: 350000,
    },
    {
        id: 'dominant-bills',
        label: 'Dominant Bills',
        description: 'Bills much larger than Spend/Goals',
        incomeTotalCents: 900000,
        billsTotalCents: 700000,
        spendTotalCents: 100000,
        goalsTotalCents: 100000,
    },
    {
        id: 'equal',
        label: 'Equal thirds',
        description: 'All three equal height',
        incomeTotalCents: 600000,
        billsTotalCents: 200000,
        spendTotalCents: 200000,
        goalsTotalCents: 200000,
    },
    {
        id: 'income-split',
        label: 'Income Split',
        description: 'isIncomeSplitEligible = true (Fruitful logo node)',
        incomeTotalCents: 750000,
        billsTotalCents: 350000,
        spendTotalCents: 150000,
        goalsTotalCents: 250000,
        isIncomeSplitEligible: true,
    },
] as const;

type Scenario = (typeof SCENARIOS)[number];

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

const ScenarioChip = ({
    label,
    isSelected,
    onPress,
}: {
    label: string;
    isSelected: boolean;
    onPress: () => void;
}) => (
    <YStack
        paddingHorizontal={12}
        paddingVertical={8}
        borderRadius={8}
        borderWidth={1.5}
        borderColor={isSelected ? '$green900' : '$gray200'}
        backgroundColor={isSelected ? '$green300' : '$white'}
        onPress={onPress}
        cursor="pointer"
    >
        <BodySmallRegular color={isSelected ? '$green900' : '$gray1100'}>
            {label}
        </BodySmallRegular>
    </YStack>
);

const FilterChip = ({
    label,
    isSelected,
    onPress,
}: {
    label: string;
    isSelected: boolean;
    onPress: () => void;
}) => (
    <YStack
        paddingHorizontal={10}
        paddingVertical={6}
        borderRadius={6}
        borderWidth={1}
        borderColor={isSelected ? '$dropletBlue900' : '$gray200'}
        backgroundColor={isSelected ? '$dropletBlue200' : '$white'}
        onPress={onPress}
        cursor="pointer"
    >
        <BodySmallRegular color={isSelected ? '$dropletBlue900' : '$gray1100'}>
            {label}
        </BodySmallRegular>
    </YStack>
);

const SectionTitle = ({
    children,
    accent,
}: {
    children: string;
    accent?: 'before' | 'after' | 'none';
}) => {
    const bg =
        accent === 'before'
            ? '$orange300'
            : accent === 'after'
              ? '$green300'
              : '$gray200';
    const color =
        accent === 'before'
            ? '$orange1000'
            : accent === 'after'
              ? '$green900'
              : '$black';
    return (
        <YStack
            paddingVertical={8}
            paddingHorizontal={12}
            borderRadius={8}
            backgroundColor={bg}
        >
            <LabelEmphasized color={color}>{children}</LabelEmphasized>
        </YStack>
    );
};

const VisualCard = ({ children }: { children: React.ReactNode }) => (
    <YStack
        flex={1}
        borderRadius={16}
        borderWidth={1}
        borderColor="$gray200"
        padding={16}
        backgroundColor="$white"
    >
        {children}
    </YStack>
);

// ---------------------------------------------------------------------------
// Side-by-side comparison — small / static context
// ---------------------------------------------------------------------------
// Old: MoneyMapPreview (smoothie, static, old colors)
// New: MoneyMapVisual variant="small" (new colors, $0 transparent bar, animations)
//
// ⚠️  Things to verify:
//   1. Color palette — new uses design tokens, old uses hardcoded hex values
//   2. $0 bar — new renders transparent placeholder; old shows no bar
//   3. Proportions / bar height range — should look similar, minor tweaks OK
//   4. No Fruitful logo node on small variant
// ---------------------------------------------------------------------------

const SmallSideBySide = ({ scenario }: { scenario: Scenario }) => (
    <XStack gap={16} flexWrap="wrap">
        <YStack flex={1} minWidth={240} gap={8}>
            <SectionTitle accent="before">
                Before — MoneyMapPreview (current production)
            </SectionTitle>
            <VisualCard>
                <MoneyMapPreview
                    incomeTotalCents={scenario.incomeTotalCents}
                    billsTotalCents={scenario.billsTotalCents}
                    spendTotalCents={scenario.spendTotalCents}
                    goalsTotalCents={scenario.goalsTotalCents}
                />
            </VisualCard>
        </YStack>
        <YStack flex={1} minWidth={240} gap={8}>
            <SectionTitle accent="after">
                After — MoneyMapVisual variant=&quot;small&quot; (new design)
            </SectionTitle>
            <VisualCard>
                <MoneyMapVisual
                    variant="small"
                    incomeTotalCents={scenario.incomeTotalCents}
                    billsTotalCents={scenario.billsTotalCents}
                    spendTotalCents={scenario.spendTotalCents}
                    goalsTotalCents={scenario.goalsTotalCents}
                />
            </VisualCard>
        </YStack>
    </XStack>
);

// ---------------------------------------------------------------------------
// Default variant — static + interactive side by side
// ---------------------------------------------------------------------------

const DefaultSideBySide = ({ scenario }: { scenario: Scenario }) => {
    const [filter, setFilter] = useState<MoneyMapFilterState>(
        MoneyMapFilterState.ALL,
    );
    const filterLabels: Array<{ filter: MoneyMapFilterState; label: string }> =
        [
            { filter: MoneyMapFilterState.ALL, label: 'All' },
            { filter: MoneyMapFilterState.BILLS, label: 'Bills' },
            { filter: MoneyMapFilterState.SPEND, label: 'Spend' },
            { filter: MoneyMapFilterState.GOALS, label: 'Goals' },
        ];
    const isIncomeSplitEligible =
        'isIncomeSplitEligible' in scenario
            ? scenario.isIncomeSplitEligible
            : false;
    return (
        <XStack gap={16} flexWrap="wrap">
            {/* Static — what most users see (no filter selection active) */}
            <YStack flex={1} minWidth={280} gap={8}>
                <SectionTitle>Static (no filter)</SectionTitle>
                <VisualCard>
                    <MoneyMapVisual
                        variant="default"
                        incomeTotalCents={scenario.incomeTotalCents}
                        billsTotalCents={scenario.billsTotalCents}
                        spendTotalCents={scenario.spendTotalCents}
                        goalsTotalCents={scenario.goalsTotalCents}
                        isIncomeSplitEligible={isIncomeSplitEligible}
                    />
                </VisualCard>
            </YStack>

            {/* Interactive — tap bars or use chips to test selection */}
            <YStack flex={1} minWidth={280} gap={8}>
                <SectionTitle accent="after">
                    Interactive (tap bars or chips)
                </SectionTitle>
                <XStack gap={8} flexWrap="wrap">
                    {filterLabels.map(({ filter: f, label }) => (
                        <FilterChip
                            key={f}
                            label={label}
                            isSelected={filter === f}
                            onPress={() => {
                                setFilter(f);
                            }}
                        />
                    ))}
                </XStack>
                <VisualCard>
                    <MoneyMapVisual
                        variant="default"
                        incomeTotalCents={scenario.incomeTotalCents}
                        billsTotalCents={scenario.billsTotalCents}
                        spendTotalCents={scenario.spendTotalCents}
                        goalsTotalCents={scenario.goalsTotalCents}
                        isIncomeSplitEligible={isIncomeSplitEligible}
                        filter={filter}
                        setFilter={setFilter}
                    />
                </VisualCard>
            </YStack>
        </XStack>
    );
};

// ---------------------------------------------------------------------------
// Small interactive
// ---------------------------------------------------------------------------

const SmallInteractive = ({ scenario }: { scenario: Scenario }) => {
    const [filter, setFilter] = useState<MoneyMapFilterState>(
        MoneyMapFilterState.ALL,
    );
    return (
        <YStack gap={8}>
            <XStack gap={8} flexWrap="wrap">
                {[
                    MoneyMapFilterState.ALL,
                    MoneyMapFilterState.BILLS,
                    MoneyMapFilterState.SPEND,
                    MoneyMapFilterState.GOALS,
                ].map((f) => (
                    <FilterChip
                        key={f}
                        label={
                            f === MoneyMapFilterState.ALL ? 'All' : f.toString()
                        }
                        isSelected={filter === f}
                        onPress={() => {
                            setFilter(f);
                        }}
                    />
                ))}
            </XStack>
            <VisualCard>
                <MoneyMapVisual
                    variant="small"
                    incomeTotalCents={scenario.incomeTotalCents}
                    billsTotalCents={scenario.billsTotalCents}
                    spendTotalCents={scenario.spendTotalCents}
                    goalsTotalCents={scenario.goalsTotalCents}
                    filter={filter}
                    setFilter={setFilter}
                />
            </VisualCard>
        </YStack>
    );
};

// ---------------------------------------------------------------------------
// Legacy vs New — default variant side by side
// ---------------------------------------------------------------------------

const LegacyVsNewDefault = ({ scenario }: { scenario: Scenario }) => {
    const [legacyFilter, setLegacyFilter] = useState<MoneyMapFilterState>(
        MoneyMapFilterState.ALL,
    );
    const [newFilter, setNewFilter] = useState<MoneyMapFilterState>(
        MoneyMapFilterState.ALL,
    );
    const isIncomeSplitEligible =
        'isIncomeSplitEligible' in scenario
            ? scenario.isIncomeSplitEligible
            : false;
    return (
        <XStack gap={16} flexWrap="wrap">
            <YStack flex={1} minWidth={280} gap={8}>
                <SectionTitle accent="before">
                    Before — Legacy MoneyMapVisual (current production)
                </SectionTitle>
                <VisualCard>
                    <LegacyMoneyMapVisual
                        incomeTotalCents={scenario.incomeTotalCents}
                        billsTotalCents={scenario.billsTotalCents}
                        spendTotalCents={scenario.spendTotalCents}
                        goalsTotalCents={scenario.goalsTotalCents}
                        filter={legacyFilter}
                        setFilter={setLegacyFilter}
                        isIncomeSplitEligible={isIncomeSplitEligible}
                    />
                </VisualCard>
            </YStack>
            <YStack flex={1} minWidth={280} gap={8}>
                <SectionTitle accent="after">
                    After — New MoneyMapVisual variant=&quot;default&quot;
                </SectionTitle>
                <VisualCard>
                    <MoneyMapVisual
                        variant="default"
                        incomeTotalCents={scenario.incomeTotalCents}
                        billsTotalCents={scenario.billsTotalCents}
                        spendTotalCents={scenario.spendTotalCents}
                        goalsTotalCents={scenario.goalsTotalCents}
                        isIncomeSplitEligible={isIncomeSplitEligible}
                        filter={newFilter}
                        setFilter={setNewFilter}
                    />
                </VisualCard>
            </YStack>
        </XStack>
    );
};

// ---------------------------------------------------------------------------
// Main gallery
// ---------------------------------------------------------------------------

const MoneyMapVisualGallery = () => {
    const [selectedScenarioId, setSelectedScenarioId] = useState<string>(
        SCENARIOS[0].id,
    );

    const scenario =
        SCENARIOS.find((s) => s.id === selectedScenarioId) ?? SCENARIOS[0];

    return (
        <ScreenContainer
            title="Money Map Visual"
            hasBackButton
            onFallbackRoute="/dev"
            maxWidthOverride={1200}
        >
            <YStack gap={24} paddingBottom={60}>
                {/* ── Scenario picker ── */}
                <YStack
                    gap={12}
                    padding={16}
                    borderRadius={12}
                    backgroundColor="$gray100"
                >
                    <LabelEmphasized>Scenario</LabelEmphasized>
                    <XStack gap={8} flexWrap="wrap">
                        {SCENARIOS.map((s) => (
                            <ScenarioChip
                                key={s.id}
                                label={s.label}
                                isSelected={selectedScenarioId === s.id}
                                onPress={() => {
                                    setSelectedScenarioId(s.id);
                                }}
                            />
                        ))}
                    </XStack>
                    <YStack gap={2}>
                        <LabelRegular color="$gray1000">
                            {scenario.description}
                        </LabelRegular>
                        <BodySmallRegular color="$gray1000">
                            Income: $
                            {(scenario.incomeTotalCents / 100).toLocaleString()}{' '}
                            · Bills: $
                            {(scenario.billsTotalCents / 100).toLocaleString()}{' '}
                            · Spend: $
                            {(scenario.spendTotalCents / 100).toLocaleString()}{' '}
                            · Goals: $
                            {(scenario.goalsTotalCents / 100).toLocaleString()}
                        </BodySmallRegular>
                    </YStack>
                </YStack>

                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                {/* SECTION 1 — Side-by-side: old MoneyMapPreview vs new small */}
                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                <YStack gap={12}>
                    <LabelEmphasized>
                        Static / embedded context (chat widgets, onboarding
                        slide, ConvMM results)
                    </LabelEmphasized>
                    <BodySmallRegular color="$gray1000">
                        These screens currently use{' '}
                        <LabelEmphasized>MoneyMapPreview</LabelEmphasized>.
                        Verify the new design looks correct before swapping to{' '}
                        <LabelEmphasized>
                            MoneyMapVisual variant=&quot;small&quot;
                        </LabelEmphasized>
                        . Check: colors, proportions, $0 bar treatment, no logo
                        node.
                    </BodySmallRegular>
                    <SmallSideBySide scenario={scenario} />
                </YStack>

                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                {/* SECTION 2 — Legacy vs New default (before / after)         */}
                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                <YStack gap={12}>
                    <LabelEmphasized>
                        Legacy vs New — Default variant (before / after)
                    </LabelEmphasized>
                    <BodySmallRegular color="$gray1000">
                        Side-by-side of the legacy production visual and the new
                        default variant. Both are interactive — tap bars to
                        compare selection behavior.
                    </BodySmallRegular>
                    <LegacyVsNewDefault scenario={scenario} />
                </YStack>

                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                {/* SECTION 3 — Default variant (static vs interactive)        */}
                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                <YStack gap={12}>
                    <LabelEmphasized>
                        Default variant (MoneyMap screen, MoneyMapWidget, Grove
                        PreviewVisual)
                    </LabelEmphasized>
                    <BodySmallRegular color="$gray1000">
                        New behaviors: 250ms opacity fade on selection,
                        selected-path drawn on top, Fruitful logo node (rounded
                        rect, green), bar border on selection.
                    </BodySmallRegular>
                    <DefaultSideBySide scenario={scenario} />
                </YStack>

                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                {/* SECTION 3 — Small variant: static / interactive / no-anim  */}
                {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
                <YStack gap={12}>
                    <LabelEmphasized>
                        New — Small variant (additional states)
                    </LabelEmphasized>
                    <XStack gap={16} flexWrap="wrap">
                        <YStack flex={1} minWidth={240} gap={8}>
                            <SectionTitle>Static</SectionTitle>
                            <VisualCard>
                                <MoneyMapVisual
                                    variant="small"
                                    incomeTotalCents={scenario.incomeTotalCents}
                                    billsTotalCents={scenario.billsTotalCents}
                                    spendTotalCents={scenario.spendTotalCents}
                                    goalsTotalCents={scenario.goalsTotalCents}
                                />
                            </VisualCard>
                        </YStack>
                        <YStack flex={1} minWidth={240} gap={8}>
                            <SectionTitle accent="after">
                                Interactive (tap labels or chips)
                            </SectionTitle>
                            <SmallInteractive scenario={scenario} />
                        </YStack>
                        <YStack flex={1} minWidth={240} gap={8}>
                            <SectionTitle>
                                animated=false (instant render)
                            </SectionTitle>
                            <VisualCard>
                                <MoneyMapVisual
                                    variant="small"
                                    animated={false}
                                    incomeTotalCents={scenario.incomeTotalCents}
                                    billsTotalCents={scenario.billsTotalCents}
                                    spendTotalCents={scenario.spendTotalCents}
                                    goalsTotalCents={scenario.goalsTotalCents}
                                />
                            </VisualCard>
                        </YStack>
                    </XStack>
                </YStack>
            </YStack>
        </ScreenContainer>
    );
};

export default function MoneyMapVisualGalleryPage() {
    if (
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'local' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'sandbox' &&
        process.env.EXPO_PUBLIC_FRUITFUL_ENV !== 'staging'
    ) {
        return <Redirect href="/" />;
    }

    return <MoneyMapVisualGallery />;
}
